import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { confirmCreateQuestions } from '../asyncActions/documentUpload';
import { RootState } from '../index';
import questionConfig from '@/components/Questions/config';

// --- Types ---

// Generate QuestionType from questionConfig keys
export type QuestionType = keyof typeof questionConfig;

export type ExtractedQuestion = {
  id: string;
  lineNumber: number;
  text: string;
  type: QuestionType | string; // widened to accept page types (welcome_text_main, etc.)
  options: string[];
  textSub?: string; // welcome subtitle
  rows?: string[]; // matrix row statements
  columns?: string[]; // matrix column scale points
  originalText?: string;
  isModified: boolean;
  isNew: boolean;
  validationError?: string;
  section?: string;
};

export type VerificationError = {
  lineNumber: number;
  questionId: string;
  message: string;
  type: 'format' | 'type' | 'content';
};

export type VerificationResult = {
  isValid: boolean;
  totalQuestions: number;
  validQuestions: number;
  errors: VerificationError[];
};

export type CreationStatus = {
  questionId: string;
  lineNumber: number;
  text: string;
  status: 'pending' | 'creating' | 'created' | 'failed';
  createdId?: number;
  error?: string;
};

export type StepType = 'upload' | 'editor' | 'creating' | 'complete';

// ProcessingPhase matches backend SSE phase values
export type ProcessingPhase = 'idle' | 'extracting' | 'analyzing' | 'parsing' | 'formatting' | 'planning' | 'generating' | 'refining';

export type FlowMode = 'upload' | 'studyBuilder';

export type ChatMessage = {
  id: string;
  role: 'user' | 'ai';
  content: string;
  timestamp: number;
};

export type DocumentUploadState = {
  // Navigation
  currentStep: StepType;

  // File Upload
  uploadedFile: { name: string; size: number; type: string } | null;
  fileUrl: string | null; // S3 URL after presigned upload
  isUploading: boolean;
  uploadProgress: number;
  fileValidationError: string | null;

  // Processing (SSE stream from /documentUpload/stream)
  processingPhase: ProcessingPhase;
  processingMessage: string;
  isStreamConnected: boolean;
  streamingContent: string; // Accumulated AI chunks during streaming
  isProcessing: boolean; // true while SSE stream is active and questions not yet received

  // Document Editor
  questions: ExtractedQuestion[];
  hasUnsavedChanges: boolean;

  // Verification (frontend-only)
  isVerifying: boolean;
  verificationResult: VerificationResult | null;

  // Creation (bulk POST /documentUpload/create)
  isCreating: boolean;
  createdQuestions: { tempId: string; questionId: number }[];

  // Section detection (when hasSection=true)
  isExtractingSections: boolean;
  detectedSections: string[];
  sectionsVerified: boolean;

  // Section creation progress
  sectionCreationProgress: { current: number; total: number; currentName: string } | null;

  // Error
  error: string | null;
  canRetry: boolean;

  // Modal state
  isModalOpen: boolean;
  modalFullScreen: boolean;
  modalSize: string;

  // Study Builder
  mode: FlowMode;
  brief: string;
  chatHistory: ChatMessage[];
  isRefining: boolean;
  refiningStatus: string;
};

const initialState: DocumentUploadState = {
  currentStep: 'upload',

  uploadedFile: null,
  fileUrl: null,
  isUploading: false,
  uploadProgress: 0,
  fileValidationError: null,

  processingPhase: 'idle',
  processingMessage: '',
  isStreamConnected: false,
  streamingContent: '',
  isProcessing: false,

  questions: [],
  hasUnsavedChanges: false,

  isVerifying: false,
  verificationResult: null,

  isCreating: false,
  createdQuestions: [],

  isExtractingSections: false,
  detectedSections: [],
  sectionsVerified: false,
  sectionCreationProgress: null,

  error: null,
  canRetry: false,

  isModalOpen: false,
  modalFullScreen: false,
  modalSize: '5xl',

  mode: 'upload',
  brief: '',
  chatHistory: [],
  isRefining: false,
  refiningStatus: '',
};

export const DocumentUploadSlice = createSlice({
  name: 'documentUpload',
  initialState,
  reducers: {
    // Modal
    openModal: (state) => {
      state.isModalOpen = true;
    },
    closeModal: (state) => {
      state.isModalOpen = false;
      state.modalFullScreen = false;
    },
    setModalFullScreen: (state, action: PayloadAction<boolean>) => {
      state.modalFullScreen = action.payload;
    },
    setModalSize: (state, action: PayloadAction<string>) => {
      state.modalSize = action.payload;
    },

    // Navigation
    setCurrentStep: (state, action: PayloadAction<StepType>) => {
      state.currentStep = action.payload;
    },

    // File Upload
    setUploadedFile: (
      state,
      action: PayloadAction<{ name: string; size: number; type: string } | null>,
    ) => {
      state.uploadedFile = action.payload;
      state.fileValidationError = null;
    },
    setFileUrl: (state, action: PayloadAction<string | null>) => {
      state.fileUrl = action.payload;
    },
    setIsUploading: (state, action: PayloadAction<boolean>) => {
      state.isUploading = action.payload;
    },
    setUploadProgress: (state, action: PayloadAction<number>) => {
      state.uploadProgress = action.payload;
    },
    setFileValidationError: (state, action: PayloadAction<string | null>) => {
      state.fileValidationError = action.payload;
    },

    // Processing (SSE stream events from /documentUpload/stream)
    setProcessingPhase: (
      state,
      action: PayloadAction<{ phase: ProcessingPhase; message: string }>,
    ) => {
      state.processingPhase = action.payload.phase;
      state.processingMessage = action.payload.message;
    },
    setStreamConnected: (state, action: PayloadAction<boolean>) => {
      state.isStreamConnected = action.payload;
    },
    appendStreamingContent: (state, action: PayloadAction<string>) => {
      state.streamingContent += action.payload;
    },
    clearStreamingContent: (state) => {
      state.streamingContent = '';
    },
    setIsProcessing: (state, action: PayloadAction<boolean>) => {
      state.isProcessing = action.payload;
    },

    // Document Editor - Questions
    setQuestions: (state, action: PayloadAction<ExtractedQuestion[]>) => {
      state.questions = action.payload;
    },
    updateQuestion: (
      state,
      action: PayloadAction<{ id: string; updates: Partial<ExtractedQuestion> }>,
    ) => {
      const index = state.questions.findIndex(
        (q) => q.id === action.payload.id,
      );
      if (index !== -1) {
        state.questions[index] = {
          ...state.questions[index],
          ...action.payload.updates,
        };
        state.hasUnsavedChanges = true;
      }
    },
    addQuestion: (state, action: PayloadAction<ExtractedQuestion>) => {
      state.questions.push(action.payload);
      // Recalculate line numbers
      state.questions.forEach((q, i) => {
        q.lineNumber = i + 1;
      });
      state.hasUnsavedChanges = true;
    },
    insertQuestionAt: (
      state,
      action: PayloadAction<{ question: ExtractedQuestion; index: number }>,
    ) => {
      state.questions.splice(action.payload.index, 0, action.payload.question);
      state.questions.forEach((q, i) => {
        q.lineNumber = i + 1;
      });
      state.hasUnsavedChanges = true;
    },
    removeQuestion: (state, action: PayloadAction<string>) => {
      state.questions = state.questions.filter(
        (q) => q.id !== action.payload,
      );
      // Recalculate line numbers
      state.questions.forEach((q, i) => {
        q.lineNumber = i + 1;
      });
      state.hasUnsavedChanges = true;
    },
    reorderQuestions: (
      state,
      action: PayloadAction<{ startIndex: number; endIndex: number }>,
    ) => {
      const { startIndex, endIndex } = action.payload;
      const result = Array.from(state.questions);
      const [removed] = result.splice(startIndex, 1);
      result.splice(endIndex, 0, removed);
      // Recalculate line numbers
      result.forEach((q, i) => {
        q.lineNumber = i + 1;
      });
      state.questions = result;
      state.hasUnsavedChanges = true;
    },
    setHasUnsavedChanges: (state, action: PayloadAction<boolean>) => {
      state.hasUnsavedChanges = action.payload;
    },

    // Verification
    setIsVerifying: (state, action: PayloadAction<boolean>) => {
      state.isVerifying = action.payload;
    },
    setVerificationResult: (
      state,
      action: PayloadAction<VerificationResult | null>,
    ) => {
      state.verificationResult = action.payload;
    },
    clearVerificationResult: (state) => {
      state.verificationResult = null;
      // Clear question validation errors
      state.questions.forEach((q) => {
        q.validationError = undefined;
      });
    },
    clearQuestionError: (state, action: PayloadAction<string>) => {
      const questionId = action.payload;
      const question = state.questions.find((q) => q.id === questionId);
      if (question) {
        question.validationError = undefined;
      }
      // Remove this question's error from verificationResult
      if (state.verificationResult) {
        state.verificationResult.errors = state.verificationResult.errors.filter(
          (e) => e.questionId !== questionId,
        );
        const formatErrors = state.verificationResult.errors.filter(
          (e) => e.type === 'format' || e.type === 'type',
        );
        state.verificationResult.isValid = formatErrors.length === 0;
        state.verificationResult.validQuestions = state.questions.filter(
          (q) => !q.validationError,
        ).length;
      }
    },

    // Creation (bulk POST /documentUpload/create)
    setIsCreating: (state, action: PayloadAction<boolean>) => {
      state.isCreating = action.payload;
    },
    setCreatedQuestions: (
      state,
      action: PayloadAction<{ tempId: string; questionId: number }[]>,
    ) => {
      state.createdQuestions = action.payload;
    },

    // Section detection
    setIsExtractingSections: (state, action: PayloadAction<boolean>) => {
      state.isExtractingSections = action.payload;
    },
    setDetectedSections: (state, action: PayloadAction<string[]>) => {
      state.detectedSections = action.payload;
    },
    setSectionsVerified: (state, action: PayloadAction<boolean>) => {
      state.sectionsVerified = action.payload;
    },
    updateQuestionSection: (
      state,
      action: PayloadAction<{ id: string; section: string }>,
    ) => {
      const question = state.questions.find(
        (q) => q.id === action.payload.id,
      );
      if (question) {
        question.section = action.payload.section;
      }
    },
    setSectionCreationProgress: (
      state,
      action: PayloadAction<{ current: number; total: number; currentName: string } | null>,
    ) => {
      state.sectionCreationProgress = action.payload;
    },

    // Error
    setError: (
      state,
      action: PayloadAction<{ message: string; canRetry: boolean }>,
    ) => {
      state.error = action.payload.message;
      state.canRetry = action.payload.canRetry;
    },
    clearError: (state) => {
      state.error = null;
      state.canRetry = false;
    },

    // Study Builder
    setMode: (state, action: PayloadAction<FlowMode>) => {
      state.mode = action.payload;
    },
    setBrief: (state, action: PayloadAction<string>) => {
      state.brief = action.payload;
    },
    addChatMessage: (state, action: PayloadAction<ChatMessage>) => {
      state.chatHistory.push(action.payload);
    },
    setIsRefining: (state, action: PayloadAction<boolean>) => {
      state.isRefining = action.payload;
      if (!action.payload) state.refiningStatus = '';
    },
    setRefiningStatus: (state, action: PayloadAction<string>) => {
      state.refiningStatus = action.payload;
    },
    clearChatHistory: (state) => {
      state.chatHistory = [];
    },

    // Reset
    resetState: () => initialState,
    resetToEditor: (state) => {
      state.currentStep = 'editor';
      state.isVerifying = false;
      state.verificationResult = null;
      state.isExtractingSections = false;
      state.sectionsVerified = false;
      state.error = null;
      state.canRetry = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Confirm Create Questions (bulk POST /documentUpload/create)
      .addCase(confirmCreateQuestions.pending, (state) => {
        state.isCreating = true;
        state.currentStep = 'creating';
      })
      .addCase(confirmCreateQuestions.fulfilled, (state, action) => {
        state.isCreating = false;
        state.createdQuestions = action.payload.created;
        state.currentStep = 'complete';
      })
      .addCase(confirmCreateQuestions.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.error.message || 'Failed to create questions';
        state.canRetry = true;
      });
  },
});

// --- Selectors ---
export const selectDocumentUpload = (state: RootState) =>
  state.documentUploadReducer;
export const selectCurrentStep = (state: RootState) =>
  state.documentUploadReducer.currentStep;
export const selectQuestions = (state: RootState) =>
  state.documentUploadReducer.questions;
export const selectHasUnsavedChanges = (state: RootState) =>
  state.documentUploadReducer.hasUnsavedChanges;
export const selectVerificationResult = (state: RootState) =>
  state.documentUploadReducer.verificationResult;
export const selectIsVerifying = (state: RootState) =>
  state.documentUploadReducer.isVerifying;
export const selectCreatedQuestions = (state: RootState) =>
  state.documentUploadReducer.createdQuestions;
export const selectError = (state: RootState) =>
  state.documentUploadReducer.error;
export const selectDetectedSections = (state: RootState) =>
  state.documentUploadReducer.detectedSections;
export const selectSectionCreationProgress = (state: RootState) =>
  state.documentUploadReducer.sectionCreationProgress;
export const selectBrief = (state: RootState) =>
  state.documentUploadReducer.brief;
export const selectChatHistory = (state: RootState) =>
  state.documentUploadReducer.chatHistory;
export const selectMode = (state: RootState) =>
  state.documentUploadReducer.mode;
export const selectIsRefining = (state: RootState) =>
  state.documentUploadReducer.isRefining;

export const {
  openModal,
  closeModal,
  setModalFullScreen,
  setModalSize,
  setCurrentStep,
  setUploadedFile,
  setFileUrl,
  setIsUploading,
  setUploadProgress,
  setFileValidationError,
  setProcessingPhase,
  setStreamConnected,
  appendStreamingContent,
  clearStreamingContent,
  setIsProcessing,
  setQuestions,
  updateQuestion,
  addQuestion,
  insertQuestionAt,
  removeQuestion,
  reorderQuestions,
  setHasUnsavedChanges,
  setIsVerifying,
  setVerificationResult,
  clearVerificationResult,
  clearQuestionError,
  setIsCreating,
  setCreatedQuestions,
  setIsExtractingSections,
  setDetectedSections,
  setSectionsVerified,
  updateQuestionSection,
  setSectionCreationProgress,
  setError,
  clearError,
  resetState,
  resetToEditor,
  setMode,
  setBrief,
  addChatMessage,
  setIsRefining,
  setRefiningStatus,
  clearChatHistory,
} = DocumentUploadSlice.actions;

export default DocumentUploadSlice.reducer;
